import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collectionService } from "@/services";
import type { ReceiptView } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canReverseCollection } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatDateTime } from "@/utils/format";
import { paymentModeLabel } from "../components/labels";
import { DayBookPrint } from "./components/DayBookPrint";
import { ReversePaymentModal, type ReversalPaymentTarget } from "../components/ReversePaymentModal";

const MODE_LABELS = ["cash", "upi", "bank_transfer", "cheque"] as const;

function todayRange(): DateRange {
  const d = new Date();
  return { start: d.toISOString(), end: d.toISOString() };
}

export default function DayBookPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const toast = useToast();
  const canReverse = canReverseCollection(role);

  const [receipts, setReceipts] = useState<ReceiptView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>(todayRange);
  const [printMode, setPrintMode] = useState(false);
  const [reversalTarget, setReversalTarget] = useState<ReversalPaymentTarget | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReceipts(await collectionService.listReceipts(range));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the day book.");
      setReceipts([]);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const modeTotals = useMemo(() => {
    const totals: Record<string, number> = { cash: 0, upi: 0, bank_transfer: 0, cheque: 0 };
    for (const r of receipts ?? []) {
      if (r.reversedAt) continue;
      totals[r.mode] = (totals[r.mode] ?? 0) + r.total;
    }
    return totals;
  }, [receipts]);

  const grandTotal = useMemo(
    () => (receipts ?? []).filter((r) => !r.reversedAt).reduce((s, r) => s + r.total, 0),
    [receipts]
  );

  const columns: DataTableColumn<ReceiptView>[] = [
    {
      id: "number",
      header: "Receipt",
      accessor: (r) => <span className="tnum text-xs font-semibold text-primary-700">{r.receiptNumber}</span>,
      sortValue: (r) => r.receiptNumber,
      sortable: true,
    },
    {
      id: "time",
      header: "Time",
      accessor: (r) => <span className="tnum whitespace-nowrap">{formatDateTime(r.createdAt)}</span>,
      sortValue: (r) => r.createdAt,
      sortable: true,
    },
    {
      id: "member",
      header: "Member",
      accessor: (r) => (
        <div>
          <span className="font-medium text-foreground-900">{r.memberName}</span>
          <span className="tnum ml-2 text-xs text-foreground-400">{r.memberCode}</span>
        </div>
      ),
      sortValue: (r) => r.memberName,
      sortable: true,
    },
    {
      id: "group",
      header: "Group",
      accessor: (r) => <span className="text-sm">{r.lines.map((l) => `${l.groupName} (${l.cycle})`).join(" · ")}</span>,
    },
    {
      id: "mode",
      header: "Mode",
      accessor: (r) => (
        <span className="whitespace-nowrap text-sm">
          {paymentModeLabel(r.mode)}
          {r.reference ? <span className="tnum ml-1.5 text-xs text-foreground-400">{r.reference}</span> : null}
        </span>
      ),
      sortValue: (r) => r.mode,
      sortable: true,
    },
    {
      id: "amount",
      header: "Amount",
      align: "right",
      accessor: (r) => <CurrencyDisplay amount={r.total} hideSymbol tone={r.reversedAt ? "danger" : "default"} />,
      sortValue: (r) => r.total,
      sortable: true,
      footerValue: (rows) => (
        <CurrencyDisplay amount={rows.filter((r) => !r.reversedAt).reduce((s, r) => s + r.total, 0)} hideSymbol />
      ),
    },
    {
      id: "collected",
      header: "Collected by",
      accessor: (r) => <span className="text-sm">{r.collectedBy}</span>,
      sortValue: (r) => r.collectedBy,
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) =>
        r.reversedAt ? (
          <Badge variant="overdue" dot>Reversed</Badge>
        ) : (
          <Badge variant="paid" dot>Active</Badge>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "right",
      width: "150px",
      hideable: false,
      accessor: (r) => (
        <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            icon={<i className="ri-eye-line" />}
            onClick={() => navigate(`/collection/receipts/${r.id}`)}
          >
            View
          </Button>
          {canReverse && !r.reversedAt ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 px-2"
              icon={<i className="ri-arrow-go-back-line" />}
              title="Delete / Revert Collection Transaction"
              onClick={() => {
                setReversalTarget({
                  id: r.id,
                  receiptNumber: r.receiptNumber,
                  memberName: r.memberName,
                  memberCode: r.memberCode,
                  amount: r.total,
                  mode: r.mode,
                  date: r.createdAt || r.receivedOn,
                  installmentInfo: r.lines.map((l) => `${l.groupName} (${l.cycle})`).join(", "),
                });
              }}
            >
              Revert
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/collection")}>
      Back
    </Button>
  );

  const breadcrumb = (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      <BreadcrumbItem label="Day book" isLast />
    </>
  );

  if (printMode) {
    return (
      <div>
        <PageHeader
          className="no-print"
          breadcrumb={breadcrumb}
          title="Day book"
          actions={
            <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => setPrintMode(false)}>
              Back
            </Button>
          }
        />
        <DayBookPrint receipts={receipts ?? []} range={range} totals={modeTotals} grandTotal={grandTotal} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumb}
        title="Day book"
        subtitle="All receipts in the selected range"
        actions={
          <>
            <div className="flex items-center gap-2">
              <DateRangePicker value={range} onChange={setRange} />
              {range.start && range.end ? (
                <Button size="sm" variant="outline" onClick={() => setRange(todayRange())}>
                  Today
                </Button>
              ) : null}
            </div>
            <Button variant="outline" size="sm" icon={<i className="ri-printer-line" />} onClick={() => setPrintMode(true)}>
              Print reconciliation
            </Button>
          </>
        }
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!receipts ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-lg bg-background-200" />
            ))}
          </div>
          <div className="h-64 animate-pulse rounded-lg bg-background-200" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <ModeCard label="Cash" amount={modeTotals.cash} icon="ri-bank-card-line" />
            <ModeCard label="UPI" amount={modeTotals.upi} icon="ri-smartphone-line" />
            <ModeCard label="Bank transfer" amount={modeTotals.bank_transfer} icon="ri-bank-line" />
            <ModeCard label="Cheque" amount={modeTotals.cheque} icon="ri-file-list-3-line" />
            <ModeCard label="Total collected" amount={grandTotal} icon="ri-arrow-up-line" highlight />
          </div>

          <Card
            title={`Receipts · ${range.start ? formatDate(range.start) : "…"}${range.end ? ` – ${formatDate(range.end)}` : ""}`}
            actions={
              <span className="text-xs text-foreground-400">
                {receipts.filter((r) => !r.reversedAt).length} active · {receipts.filter((r) => r.reversedAt).length} reversed
              </span>
            }
          >
            <DataTable<ReceiptView>
              columns={columns}
              rows={receipts}
              rowKey={(r) => r.id}
              emptyMessage="No receipts in this range"
              emptyDescription="Try a wider date range, or record a payment."
              showTotals
              totalLabel="Total"
              exportable
              exportFilename="day-book"
              defaultPageSize={15}
              onRowClick={(r) => navigate(`/collection/receipts/${r.id}`)}
            />
          </Card>
        </div>
      )}

      <ReversePaymentModal
        open={Boolean(reversalTarget)}
        target={reversalTarget}
        onClose={() => setReversalTarget(null)}
        onSuccess={() => load()}
      />
    </div>
  );
}

function ModeCard({ label, amount, icon, highlight }: { label: string; amount: number; icon: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-primary-200 bg-primary-50/40" : "border-background-200 bg-background-50"}`}>
      <div className="flex items-center gap-2">
        <div className={`flex h-7 w-7 items-center justify-center rounded-md ${highlight ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-700"}`}>
          <i className={`${icon} text-sm`} />
        </div>
        <p className="text-xs text-foreground-500">{label}</p>
      </div>
      <p className="tnum mt-1.5 text-lg font-semibold text-foreground-950">
        <CurrencyDisplay amount={amount} hideSymbol />
      </p>
    </div>
  );
}